import {
  errorMessageOf,
  parseSearchString,
  stringifySearchObject,
} from "@birdwatch/common";
import * as Sentry from "@sentry/react";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import {
  recoverFromStaleChunk,
  subscribeToPendingUpdate,
} from "@/features/shared/app-version";
import { bootImpersonationFrame } from "@/features/shared/impersonation";
import { env } from "./env";
import "./global.css";
import "./instrument.js";
import { routeTree } from "./routeTree.gen";

// Set up a Router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultPreload: "intent",
  stringifySearch: stringifySearchObject,
  parseSearch: parseSearchString,
});

// A new deploy is applied passively: on the user's next page navigation, and on
// a route chunk the newer deployment has already replaced. Wired here rather
// than in a component so every route tree is covered. Both live for the
// document's lifetime, so the disposers are intentionally dropped.
subscribeToPendingUpdate(router);
recoverFromStaleChunk(router);

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app")!;

/**
 * Shown in place of the app when this document is the admin impersonation
 * frame and the host never handed it a session. Plain DOM on purpose: nothing
 * of the app may render until the identity it would run under is known.
 */
function renderImpersonationBootError(error: unknown) {
  const message = document.createElement("p");
  message.style.cssText =
    "margin:48px auto;max-width:480px;font:14px/1.5 system-ui,sans-serif;color:#444;text-align:center";
  message.textContent = `Could not start the impersonation session (${errorMessageOf(
    error,
  )}). Close this view and try again.`;
  rootElement.replaceChildren(message);
}

async function start() {
  if (rootElement.innerHTML) return;

  // In the impersonation frame this completes the handshake with the admin
  // host before anything renders, so the first `/auth/me` already resolves as
  // the impersonated user. Everywhere else it returns immediately.
  try {
    await bootImpersonationFrame();
  } catch (error) {
    renderImpersonationBootError(error);
    return;
  }

  const options = env.PUBLIC_SENTRY_ENABLED
    ? {
        // Callback called when an error is thrown and not caught by an ErrorBoundary.
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          console.error("Uncaught error", error, errorInfo.componentStack);
        }),
        // Callback called when React catches an error in an ErrorBoundary.
        onCaughtError: Sentry.reactErrorHandler(),
        // Callback called when React automatically recovers from errors.
        onRecoverableError: Sentry.reactErrorHandler(),
      }
    : {};
  const root = ReactDOM.createRoot(rootElement, options);
  root.render(<RouterProvider router={router} />);
}

void start();
